import { type ColumnDef } from "@tanstack/react-table";
import type { ApiNotificationRule, AlertType, AlertFrequency } from "../types";
import type { TFunction } from "i18next";
import { Badge, badgeVariants } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import { ArrowUpDown, CircleEllipsis, PenBox } from "lucide-react";
import type { VariantProps } from "class-variance-authority";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { NavLink } from "react-router-dom";
import { ROUTES } from "@/constants/routes";

type BadgeVariant = VariantProps<typeof badgeVariants>["variant"];

function alertTypeBadgeVariant(alertType: AlertType): BadgeVariant {
  switch (alertType) {
    case "new_dilemma":
      return "success";
    case "focus_dilemma":
      return "primary_light";
    case "admin_action":
      return "secondary";
  }
}

function alertTypeLabel(alertType: AlertType, t: TFunction): string {
  switch (alertType) {
    case "new_dilemma":
      return t("modules.alerts.keys.alert_type_new_dilemma");
    case "focus_dilemma":
      return t("modules.alerts.keys.alert_type_focus_dilemma");
    case "admin_action":
      return t("modules.alerts.keys.alert_type_admin_action");
  }
}

function frequencyLabel(frequency: AlertFrequency, t: TFunction): string {
  switch (frequency) {
    case "immediate":
      return t("modules.alerts.keys.frequency_immediate");
    case "weekly":
      return t("modules.alerts.keys.frequency_weekly");
    case "monthly":
      return t("modules.alerts.keys.frequency_monthly");
  }
}

export { alertTypeBadgeVariant };

export const alertsColumns = (
  t: TFunction,
): ColumnDef<ApiNotificationRule>[] => [
  {
    accessorKey: "title",
    header: t("modules.alerts.keys.title"),
    cell: ({ row }) => (
      <span className="font-medium">{row.getValue("title")}</span>
    ),
  },
  {
    accessorKey: "message",
    header: t("modules.alerts.keys.message"),
    cell: ({ row }) => (
      <span className="text-gray-500 truncate inline-block max-w-56">
        {row.getValue("message")}
      </span>
    ),
  },
  {
    accessorKey: "frequency",
    header: t("modules.alerts.keys.frequency"),
    cell: ({ row }) => (
      <Badge variant="outline">
        {frequencyLabel(row.getValue("frequency"), t)}
      </Badge>
    ),
  },
  {
    accessorKey: "alert_type",
    header: t("modules.alerts.keys.alert_type"),
    cell: ({ row }) => {
      const alertType = row.getValue("alert_type") as AlertType;
      return (
        <Badge variant={alertTypeBadgeVariant(alertType)}>
          {alertTypeLabel(alertType, t)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="flex items-center gap-1 cursor-pointer"
      >
        {t("keys.creation_date")}
        <ArrowUpDown
          className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
        />
      </button>
    ),
    cell: ({ row }) => (
      <span className="text-gray-500">
        {formatDate(row.getValue("created_at"))}
      </span>
    ),
  },
  {
    id: "actions",
    header: t("keys.table.actions"),
    cell: ({ row }) => {
      const alert = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Ouvrir le menu</span>
              <CircleEllipsis className="h-10 w-10 rotate-90 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <NavLink to={ROUTES.ALERT_DETAILS(alert.id)} end>
                <PenBox />
                {t("keys.table.edit")}
              </NavLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
